import React, { useState } from 'react';
import { Target, Pencil, Power } from 'lucide-react';
import { formatMWK, formatHm } from './tokens';

/**
 * Screen 2 — bottom card shown when driver is ONLINE / waiting for a request.
 * - "Waiting for a ride request..." headline
 * - 3 metric chips (Trips, Earned, Online)
 * - Daily-goal progress bar with edit affordance
 * - Compact "Go Offline" link in the corner so the action is always reachable
 */
const OnlineWaitingCard = ({
  metrics = { trips_today: 0, earned_today: 0, online_seconds: 0, daily_goal: 0, progress_pct: 0 },
  onEditGoal,
  onGoOffline,
}) => {
  const goal = metrics.daily_goal || 0;
  const progress = Math.min(100, Math.max(0, metrics.progress_pct || 0));
  const goalLabel = goal > 0
    ? `Daily goal: ${formatMWK(goal)} — ${progress.toFixed(0)}% there`
    : 'Set a daily earnings goal to track your day';

  return (
    <div
      data-testid="driver-online-card"
      className="bg-white/95 backdrop-blur-md rounded-3xl p-5 shadow-[0_12px_40px_rgba(0,0,0,0.18)] border border-gray-100"
    >
      {/* Headline + offline action */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5 align-middle animate-pulse" />
            You're online
          </p>
          <h2 className="text-[19px] font-semibold text-gray-900 mt-0.5 leading-tight">
            Waiting for a ride request…
          </h2>
        </div>
        <button
          type="button"
          onClick={onGoOffline}
          data-testid="driver-go-offline-btn"
          className="shrink-0 inline-flex items-center gap-1.5 px-3 h-10 rounded-full text-[12px] font-semibold text-rose-600 bg-rose-50 hover:bg-rose-100 active:bg-rose-200 transition-colors"
          aria-label="Go offline"
        >
          <Power className="w-3.5 h-3.5" strokeWidth={2.5} />
          End shift
        </button>
      </div>

      {/* Metric chips */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <MetricChip
          label="Trips"
          value={metrics.trips_today ?? 0}
          testid="driver-metric-trips"
        />
        <MetricChip
          label="Earned"
          value={formatMWK(metrics.earned_today)}
          testid="driver-metric-earned"
        />
        <MetricChip
          label="Online"
          value={formatHm(metrics.online_seconds)}
          testid="driver-metric-online"
        />
      </div>

      {/* Goal progress */}
      <div className="bg-gray-50 rounded-2xl p-3" data-testid="driver-goal-block">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-gray-700">
            <Target className="w-3.5 h-3.5" />
            {goalLabel}
          </span>
          <button
            type="button"
            onClick={onEditGoal}
            data-testid="driver-edit-goal-btn"
            className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 hover:text-emerald-800"
          >
            {goal > 0 ? 'Edit' : 'Set goal'}
            <Pencil className="w-3 h-3" />
          </button>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full transition-[width] duration-700 ease-out"
            style={{ width: `${progress}%` }}
            data-testid="driver-goal-progress"
            aria-valuenow={progress}
            aria-valuemin={0}
            aria-valuemax={100}
            role="progressbar"
          />
        </div>
      </div>
    </div>
  );
};

const MetricChip = ({ label, value, testid }) => (
  <div className="bg-emerald-50/70 rounded-2xl py-2.5 px-2 text-center">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700/80">{label}</p>
    <p className="text-[18px] font-bold text-gray-900 mt-0.5 leading-tight tabular-nums truncate" data-testid={testid}>
      {value}
    </p>
  </div>
);

/**
 * Inline modal for setting a daily goal. Used by parent home screen.
 * Kept here to colocate with its trigger.
 */
export const GoalEditorModal = ({ open, currentGoal = 0, onSave, onClose }) => {
  const [val, setVal] = useState(String(currentGoal || ''));
  if (!open) return null;
  const presets = [10000, 20000, 30000, 50000];
  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-end sm:items-center justify-center p-3" onClick={onClose}>
      <div
        className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="driver-goal-modal"
      >
        <h3 className="text-lg font-semibold text-gray-900">Set your daily goal</h3>
        <p className="text-xs text-gray-500 mt-0.5 mb-4">
          We'll track your progress against this every day. Reset at midnight.
        </p>
        <div className="flex flex-wrap gap-2 mb-3">
          {presets.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setVal(String(p))}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                Number(val) === p ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-700 border-gray-200 hover:border-emerald-400'
              }`}
              data-testid={`driver-goal-preset-${p}`}
            >
              K{p.toLocaleString()}
            </button>
          ))}
        </div>
        <input
          type="number"
          min={0}
          step={1000}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="MWK"
          data-testid="driver-goal-input"
          className="w-full h-12 px-4 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-base tabular-nums"
        />
        <div className="flex gap-2 mt-4">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-12 rounded-full bg-gray-100 hover:bg-gray-200 text-sm font-semibold text-gray-700"
            data-testid="driver-goal-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave(Number(val) || 0)}
            className="flex-1 h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
            data-testid="driver-goal-save"
          >
            Save goal
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnlineWaitingCard;
